(function () {
  'use strict';

  const page = (window.FoxTheme && window.FoxTheme.page) || {};

  const STORAGE_KEY = 'seo_index_priority_v1';

  function getData() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored
        ? JSON.parse(stored)
        : {
            pages: {},
            globalStats: { avgBounceRate: 0.5, avgTimeOnPage: 30 },
          };
    } catch {
      return {
        pages: {},
        globalStats: { avgBounceRate: 0.5, avgTimeOnPage: 30 },
      };
    }
  }

  function saveData(data) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Silent fail
    }
  }

  // Track page performance metrics
  function trackPageMetrics() {
    const pageKey = window.location.pathname;
    const data = getData();

    if (!data.pages[pageKey]) {
      data.pages[pageKey] = {
        visits: 0,
        totalTimeOnPage: 0,
        bounces: 0,
        conversions: 0,
        priority: 0.5,
        lastUpdated: Date.now(),
      };
    }

    const pageData = data.pages[pageKey];
    pageData.visits++;

    // Track time on page
    const startTime = Date.now();
    let hasEngaged = false;

    // Detect engagement
    const engagementEvents = ['scroll', 'click', 'touchstart', 'keydown'];
    const onEngagement = () => {
      hasEngaged = true;
      engagementEvents.forEach((event) => {
        document.removeEventListener(event, onEngagement);
      });
    };

    engagementEvents.forEach((event) => {
      document.addEventListener(event, onEngagement, { once: true, passive: true });
    });

    // Track on page unload
    const trackExit = () => {
      const timeOnPage = (Date.now() - startTime) / 1000;
      pageData.totalTimeOnPage += timeOnPage;

      // Count as bounce if no engagement and left quickly
      if (!hasEngaged && timeOnPage < 5) {
        pageData.bounces++;
      }

      // Detect conversion (reached checkout, thank you page, etc.)
      const isConversion =
        window.location.pathname.includes('checkout') ||
        window.location.pathname.includes('thank') ||
        window.location.pathname.includes('success');

      if (isConversion) {
        pageData.conversions++;
      }

      // Calculate priority score
      updatePriority(pageKey, pageData);
      saveData(data);
    };

    window.addEventListener('beforeunload', trackExit);
    window.addEventListener('pagehide', trackExit);

    // Also track periodically for long sessions
    setTimeout(() => {
      if (hasEngaged) {
        trackExit();
      }
    }, 60000); // After 1 minute
  }

  // Calculate page priority score
  function updatePriority(pageKey, pageData) {
    if (pageData.visits === 0) return;

    const avgTimeOnPage = pageData.totalTimeOnPage / pageData.visits;
    const bounceRate = pageData.bounces / pageData.visits;
    const conversionRate = pageData.conversions / pageData.visits;

    // Weighted scoring
    let score = 0;

    // Time on page (0-0.3)
    score += Math.min(avgTimeOnPage / 120, 1) * 0.3;

    // Low bounce rate (0-0.3)
    score += (1 - bounceRate) * 0.3;

    // Conversion rate (0-0.4)
    score += Math.min(conversionRate * 10, 1) * 0.4;

    pageData.priority = Math.max(0.1, Math.min(1, score));
    pageData.lastUpdated = Date.now();
  }

  // Get page priority
  function getPagePriority() {
    const pageKey = window.location.pathname;
    const data = getData();
    const pageData = data.pages[pageKey];

    if (!pageData || pageData.visits < 5) {
      // Default priorities by page type
      switch (page.template) {
        case 'index':
          return 1.0;
        case 'product':
          return 0.8;
        case 'collection':
          return 0.7;
        case 'article':
          return 0.6;
        default:
          return 0.5;
      }
    }

    return pageData.priority;
  }

  // Set crawl priority hints
  function setCrawlHints() {
    const priority = getPagePriority();

    // Add meta tag for crawl priority
    let crawlMeta = document.querySelector('meta[name="robots-crawl-priority"]');
    if (!crawlMeta) {
      crawlMeta = document.createElement('meta');
      crawlMeta.name = 'robots-crawl-priority';
      document.head.appendChild(crawlMeta);
    }

    if (priority >= 0.8) {
      crawlMeta.content = 'high';
    } else if (priority >= 0.5) {
      crawlMeta.content = 'medium';
    } else {
      crawlMeta.content = 'low';
    }

    // Update existing meta robots if needed
    const robotsMeta = document.querySelector('meta[name="robots"]');
    if (robotsMeta) {
      let content = robotsMeta.content;

      // Add crawl hints
      if (priority >= 0.8 && !content.includes('max-image-preview')) {
        content += ', max-image-preview:large, max-snippet:-1';
        robotsMeta.content = content;
      }
    }

    // Add structured data with priority hints
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      url: window.location.href,
      mainContentOfPage: {
        '@type': 'WebPageElement',
        cssSelector: 'main, #MainContent',
      },
      significantLinks: priority >= 0.8 ? window.location.href : undefined,
      potentialAction:
        priority >= 0.8
          ? {
              '@type': 'ReadAction',
              target: window.location.href,
            }
          : undefined,
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'priority-hints';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);
  }

  // Generate dynamic XML sitemap hints (stored for batch submission)
  function updateSitemapHints() {
    const pageKey = window.location.pathname;
    const priority = getPagePriority();
    const data = getData();

    // Store sitemap data
    if (!data.sitemap) {
      data.sitemap = {};
    }

    data.sitemap[pageKey] = {
      priority: priority.toFixed(2),
      changefreq: priority >= 0.8 ? 'daily' : priority >= 0.5 ? 'weekly' : 'monthly',
      lastmod: new Date().toISOString().split('T')[0],
    };

    saveData(data);
  }

  // Add preload hints for high-priority pages
  function addPreloadHints() {
    const priority = getPagePriority();

    if (priority >= 0.8) {
      // High priority pages get aggressive preloading
      const links = document.querySelectorAll('a[href^="/"]');
      const highPriorityLinks = Array.from(links).slice(0, 5);

      highPriorityLinks.forEach((link) => {
        const preloadLink = document.createElement('link');
        preloadLink.rel = 'prefetch';
        preloadLink.href = link.href;
        document.head.appendChild(preloadLink);
      });
    }
  }

  // Report to console (for monitoring)
  function reportPriority() {
    // Tracking priority silently (no-op placeholder for future console/monitoring hook)
  }

  // Initialize
  function init() {
    trackPageMetrics();
    setCrawlHints();
    updateSitemapHints();
    addPreloadHints();
    reportPriority();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
